import React from 'react';
import { CheckCircle, AlertTriangle, Clock, Plus, Wine, Shield, FileText } from 'lucide-react';
interface LabelComplianceCardProps {
  onEditLabel: () => void;
}
export function LabelComplianceCard({
  onEditLabel
}: LabelComplianceCardProps) {
  const labelElements = [{
    element: 'Brand Name (Sonoma Valley Vineyards)',
    status: 'validated',
    agency: 'TTB',
    lastValidated: '2 days ago',
    details: 'Brand name registered and approved'
  }, {
    element: 'Varietal Designation (Cabernet Sauvignon)',
    status: 'validated',
    agency: 'TTB',
    lastValidated: '2 days ago',
    details: '85% minimum varietal content verified'
  }, {
    element: 'Appellation of Origin (Alexander Valley)',
    status: 'validated',
    agency: 'TTB',
    lastValidated: '2 days ago',
    details: '85% of grapes sourced from designated AVA'
  }, {
    element: 'Alcohol Content (14.5% ALC/VOL)',
    status: 'validated',
    agency: 'TTB',
    lastValidated: '2 days ago',
    details: 'Within 0.5% tolerance of actual alcohol content'
  }, {
    element: 'Government Warning Statement',
    status: 'validated',
    agency: 'TTB',
    lastValidated: '2 days ago',
    details: 'Required health warning statement present and compliant'
  }, {
    element: 'Sulfite Declaration',
    status: 'validated',
    agency: 'FDA',
    lastValidated: '2 days ago',
    details: 'Required allergen statement present'
  }, {
    element: '"Estate Reserve" Designation',
    status: 'issues',
    agency: 'TTB',
    lastValidated: '1 day ago',
    details: 'Need to verify 100% estate grown grapes for "Estate" claim'
  }, {
    element: '"Sustainably Farmed" Statement',
    status: 'pending',
    agency: 'TTB',
    lastValidated: 'Never',
    details: 'Awaiting certification documentation'
  }];
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'validated':
        return <CheckCircle className="w-4 h-4 text-green-600" data-id="element-309" />;
      case 'issues':
        return <AlertTriangle className="w-4 h-4 text-red-600" data-id="element-310" />;
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-600" data-id="element-311" />;
      default:
        return null;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'validated':
        return 'bg-green-100 text-green-800';
      case 'issues':
        return 'bg-red-100 text-red-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  const validatedCount = labelElements.filter(c => c.status === 'validated').length;
  const issuesCount = labelElements.filter(c => c.status === 'issues').length;
  const pendingCount = labelElements.filter(c => c.status === 'pending').length;
  return <div className="bg-white rounded-lg border" data-id="element-312">
      <div className="p-6 border-b border-gray-200" data-id="element-313">
        <div className="flex items-center justify-between" data-id="element-314">
          <div data-id="element-315">
            <h2 className="text-lg font-semibold text-gray-900" data-id="element-316">
              Label Compliance
            </h2>
            <div className="flex items-center space-x-4 mt-1 text-sm" data-id="element-317">
              <span className="text-green-600" data-id="element-318">{validatedCount} validated</span>
              <span className="text-red-600" data-id="element-319">{issuesCount} issues</span>
              <span className="text-yellow-600" data-id="element-320">{pendingCount} pending</span>
            </div>
          </div>
          <div className="flex items-center space-x-3" data-id="element-321">
            <button onClick={onEditLabel} className="flex items-center space-x-2 px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg" data-id="element-322">
              <FileText className="w-4 h-4" data-id="element-323" />
              <span data-id="element-324">Edit Label</span>
            </button>
          </div>
        </div>
      </div>
      <div className="divide-y divide-gray-200" data-id="element-325">
        {labelElements.map((item, index) => <div key={index} className="p-6" data-id="element-326">
            <div className="flex items-start justify-between" data-id="element-327">
              <div className="flex-1" data-id="element-328">
                <div className="flex items-center space-x-3 mb-2" data-id="element-329">
                  {getStatusIcon(item.status)}
                  <span className="font-medium text-gray-900" data-id="element-330">
                    {item.element}
                  </span>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(item.status)}`} data-id="element-331">
                    {item.status === 'validated' ? 'Validated' : item.status === 'issues' ? 'Issues' : 'Pending'}
                  </span>
                </div>
                <p className="text-sm text-gray-600 mb-2" data-id="element-332">{item.details}</p>
                <div className="flex items-center space-x-4" data-id="element-333">
                  <p className="text-xs text-gray-500" data-id="element-334">
                    Last validated: {item.lastValidated}
                  </p>
                  <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-purple-100 text-purple-800" data-id="element-335">
                    {item.agency}
                  </span>
                </div>
              </div>
              <button className="text-purple-600 hover:text-purple-700 text-sm font-medium ml-4" data-id="element-336">
                Review
              </button>
            </div>
          </div>)}
      </div>
      <div className="p-6 border-t border-gray-200 bg-gray-50" data-id="element-337">
        <div className="flex items-center justify-between text-sm" data-id="element-338">
          <div className="flex items-center space-x-2" data-id="element-339">
            <Wine className="w-4 h-4 text-purple-600" data-id="element-340" />
            <span className="text-gray-700" data-id="element-341">
              TTB Certificate of Label Approval (COLA) ID: TTB-ID-12345678
            </span>
          </div>
          <span className="text-green-600 font-medium" data-id="element-342">Approved</span>
        </div>
      </div>
    </div>;
}